<template>
  <div class="matter-detail">
    <van-image width="100%" height="375px" :src="benefitHeaderImage" />
    <div class="matter-info card">
      <div class="tip" v-if="amount > 0">剩{{ amount }}次</div>
      <div class="price-wrap">
        <div class="price">
          <span class="unit">￥</span>
          <span>{{ selectSku.item_price }}</span>
        </div>
        <div class="face-value">
          <span>¥ {{ selectSku.face_value }}</span>
        </div>
      </div>
      <div class="matter-name">{{ benefitInfo.title || benefitInfo.name }}</div>
      <div class="use-time">{{ getSecondTitle }}</div>
    </div>
    <div class="select-wrap" v-if="sku.length > 1">
      <div
        class="select-item"
        v-for="item of sku"
        :key="item.item_code"
        @click="handleSelectSku(item)"
        :class="{ active: selectSku.item_code === item.item_code }"
      >
        <img :src="item.image_url" />
        <div class="select-item-name">
          {{ item.item_name }}
        </div>
      </div>
    </div>
    <MatterIntroduction :benefitInfo="benefitInfo" :selectSku="selectSku" />
    <div class="btn-wrap">
      <div class="btn" @click="showDrawPopup = true">
        {{ drawBtnText }}
      </div>
    </div>
    <DrawPopup
      :show.sync="showDrawPopup"
      @showAddressPopup="showAddressPopup = true"
      :selectSku="selectSku"
      :benefitInfo.sync="benefitInfo"
      :addressList="addressList"
      :selectAddress="selectAddress"
    />
    <AddressPopup
      :show.sync="showAddressPopup"
      :addressList="addressList"
      :selectAddress="selectAddress"
      @handleSelectAddress="handleSelectAddress"
      :getUserAddressList="getUserAddressList"
    />
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  computed,
} from "@vue/composition-api";
import Vue from "vue";

import { getBenefitInfo, getNamespaceBenefitImage } from "@/apis";
import { useRoute, useStore } from "@/components/use/base";
import { getUserAddress } from "@/apis/matter";

import MatterIntroduction from "./MatterIntroduction.vue";
import DrawPopup from "./DrawPopup.vue";
import AddressPopup from "./AddressPopup.vue";

const vm = Vue.prototype;

export default defineComponent({
  props: ["sku"],
  components: {
    DrawPopup,
    AddressPopup,
    MatterIntroduction,
  },
  setup(props) {
    const route = useRoute();
    const store = useStore();

    const showDrawPopup = ref(false);
    const showAddressPopup = ref(false);
    const benefitInfo = ref({});
    const images = ref({});
    const selectSku = ref({});
    const addressList = ref([]);
    const selectAddress = ref({});

    const { benefit_type } = route.query;

    const namespace = computed(() => store.state.namespace);
    const userInfo = computed(() => store.state.userInfo);

    const benefitHeaderImage = computed(() => {
      return (
        images.value.header?.[0].image_url ||
        `${vm.$cdn}/static/${namespace.value}/${benefit_type}-bg2.png`
      );
    });

    const amount = computed(() => {
      return (
        benefitInfo.value.amount_available + benefitInfo.value.amount_reserved
      );
    });

    const benefitIcon = computed(() => {
      return images.value.titleIcon?.[0].image_url;
    });

    const getSecondTitle = computed(() => {
      if (!userInfo.value.is_member) {
        return "您还不是会员";
      }
      if (!benefitIcon.value) {
        return benefitInfo.value.title || benefitInfo.value.name;
      }
      if (benefitInfo.value.brief_subtitle) {
        return benefitInfo.value.brief_subtitle;
      }
      if (
        benefitInfo.value.amount_available <= 0 &&
        benefitInfo.value.amount_reserved <= 0
      ) {
        return "本权益已用完";
      }
      const unit = benefitInfo.value.use_period_frequency
        ? `本${benefitInfo.value.use_period_frequency}`
        : "当前";
      return [
        `${unit}可用${benefitInfo.value.amount_available}次`,
        benefitInfo.value.next_use_wait_days !== null &&
          `距下${benefitInfo.value.use_period_frequency || "次"}使用还有${
            benefitInfo.value.next_use_wait_days
          }天`,
      ]
        .filter(Boolean)
        .join("，");
    });

    const drawBtnText = computed(() => {
      const price = Number(selectSku.value.item_price);
      return price > 0 ? "立即购买" : "立即领取";
    });

    onMounted(async () => {
      selectSku.value = props.sku[0];
      await initSkuInfo();
      await getUserAddressList();
    });

    const handleSelectSku = (item) => {
      selectSku.value = item;
    };

    const getUserAddressList = async () => {
      const { data } = await getUserAddress();
      addressList.value = data;
      selectAddress.value = data[0] || {};
    };

    const handleSelectAddress = (addressObj) => {
      selectAddress.value = addressObj;
      showAddressPopup.value = false;
    };

    const initSkuInfo = async () => {
      const loading = vm.$loading();
      try {
        const res = await getNamespaceBenefitImage({
          namespace_en: namespace.value,
          product_code: benefit_type,
        });
        images.value = res.data;
        await fetchBenefitInfo({ benefit_type });
      } catch (error) {
        console.log(error);
      } finally {
        loading.close();
      }
    };

    const fetchBenefitInfo = async (params) => {
      try {
        const res = await getBenefitInfo(params);
        benefitInfo.value = res.data;
        return res;
      } catch (e) {
        return { data: {} };
      }
    };

    return {
      showDrawPopup,
      showAddressPopup,
      benefitInfo,
      benefitHeaderImage,
      getSecondTitle,
      selectSku,
      addressList,
      selectAddress,
      drawBtnText,
      amount,
      fetchBenefitInfo,
      handleSelectAddress,
      getUserAddressList,
      handleSelectSku,
    };
  },
});
</script>

<style lang="less" scoped>
.matter-detail {
  padding-bottom: 113px;
}

.matter-info {
  position: relative;

  .tip {
    position: absolute;
    top: 0;
    right: 0;
    background: linear-gradient(90deg, #feedd7 0%, #ffffff 100%);
    border-radius: 0px 9px 0px 6px;
    padding: 2px 9px;
    font-weight: 400;
    font-size: 11px;
    line-height: 16px;
    color: #844f16;
    text-align: center;
  }
}

.image-wrap {
  height: 375px;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card {
  margin: 12px 12px 0 12px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  padding: 16px;
}

.price-wrap {
  display: flex;
  align-items: flex-end;
  .price {
    font-weight: 600;
    font-size: 24px;

    color: #fa541c;
    .unit {
      font-size: 16px;
    }
  }

  .face-value {
    text-decoration: line-through;
    font-weight: 400;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.4);
    margin-left: 12px;
    margin-bottom: 2px;
  }
}

.matter-name {
  font-weight: 500;
  font-size: 14px;
  color: #000000;
  margin-top: 10px;
}

.use-time {
  margin-top: 7px;
  font-weight: 400;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 17px;
}

.select-wrap {
  background: #ffffff;
  border-radius: 6px;
  padding: 16px;
  margin: 12px 12px 0 12px;

  .select-item {
    padding: 6px;
    background: #ffffff;
    border-radius: 4px;
    border: 1px solid #d2d2d2;
    font-weight: 400;
    font-size: 12px;
    color: #333333;
    display: flex;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 12px;
    }

    &.active {
      background: #fff7ed;
      border-radius: 4px;
      border: 1px solid #fa541c;
      color: #fa541c;
    }

    img {
      width: 32px;
      height: 32px;
      border-radius: 2px;
      border: none;
      margin-right: 6px;
    }
  }
}

.btn-wrap {
  height: 97px;
  background: #ffffff;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;

  .btn {
    margin: 0 auto;
    margin-top: 13px;
    width: 351px;
    height: 44px;
    background: linear-gradient(115deg, #dfbd94 0%, #ffe8c8 100%);
    border-radius: 21px;
    text-align: center;

    font-weight: 500;
    font-size: 16px;
    line-height: 44px;
  }
}
</style>
